import React, { useState, useEffect, useContext, createContext } from 'react';
import { app } from '@BUP/configs/Firebase';
import { BupLoading } from '@BUP/components';
import { notification } from 'antd';
import {
  firebaseSignIn,
  firebaseSignUp,
  firebaseSignInProvider,
  firebaseSignOut,
  firebaseSendPasswordResetEmail,
  firebaseConfirmPasswordReset,
  firebaseGetCurrentUser,
  firebaseGetCurrentUserToken,
  firebaseRestorePassword,
} from '@BUP/services/Firebase';
import {
  getUserByFirebaseID,
  createUserByInvitationCode,
  createUserWhitOutInvitationCode,
} from '@BUP/services/BraveUp';
import { BraveBupAPI, SociogramApi } from '@BUP/services/Axios';
import { LoginLanguageContext } from '@BUP/contexts';

export const AuthContext = createContext(null);

export const useAuth = () => {
  return useContext(AuthContext);
};

function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const { selectedEnglishLanguage } = useContext(LoginLanguageContext);
  const setRequestsToken = async (user) => {
    return new Promise(async (resolve) => {
      const token = user && (await firebaseGetCurrentUserToken());
      if (token) {
        setAccessToken(token);
        BraveBupAPI.defaults.headers.common.Authorization = `Bearer ${token}`;
        SociogramApi.defaults.headers.common.Authorization = `Bearer ${token}`;
      } else {
        BraveBupAPI.defaults.headers.common.Authorization = null;
        SociogramApi.defaults.headers.common.Authorization = `Bearer ${token}`;
      }
      resolve();
    });
  };

  const getOrCreateUser = async (user, strategy) => {
    if (!user) return null;
    setIsLoading(true);
    try {
      await setRequestsToken(user);
      const braveUpUser = await getUserByFirebaseID(user);
      if (braveUpUser) {
        // console.log("hay braveUpUser", braveUpUser)
        return braveUpUser;
      }
      // console.log("no hay braveUpUser", braveUpUser)
      if (strategy) {
        // console.log("se deberia crear")
        const { name: strategyName, data } = strategy;
        const { name, code } = data;
        switch (strategyName) {
          case 'email':
            await createUserByInvitationCode(user, name, code);
            // console.log("se creo usuario con codigo")
            break;
          default:
            await createUserWhitOutInvitationCode(user);
            // console.log("se creo usuario sin codigo");
            break;
        }
        // console.log("last phase")
        const newBraveUpUser = await getUserByFirebaseID(user);
        // console.log("last newBraveUpUser", newBraveUpUser)
        return newBraveUpUser;
      }
    } catch (e) {
      console.log('getOrCreateUser error', e);
      if (selectedEnglishLanguage)
        notification.info({
          style: { fontWeight: 500, fontSize: 17 },
          message: e.title || 'FAILED TO LOGIN',
          description:
            e.message || 'There was an error logging in, please try again.',
          duration: 10,
        });
      else
        notification.info({
          style: { fontWeight: 500, fontSize: 17 },
          message: e.title || 'ERROR AL INICIAR SESIÓN',
          description:
            e.message ||
            'Ocurrió un error al iniciar sesion, por favor vuelve a intentarlo.',
          duration: 10,
        });
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      await firebaseSignIn(email, password);
    } catch (e) {
      // console.log("Error signIn", e)
      throw e;
    }
  };

  const signUp = async (email, password, name, code) => {
    try {
      const { user: firebaseUser } = await firebaseSignUp(email, password);
      const newUser = await getOrCreateUser(firebaseUser, {
        name: 'email',
        data: { name, code },
      });
      setUser(newUser);
    } catch (e) {
      throw e;
    }
  };

  const signInProvider = async (provider) => {
    try {
      const { user: firebaseUser } = await firebaseSignInProvider(provider);
      const newUser = await getOrCreateUser(firebaseUser, {
        name: 'provider',
        data: {},
      });
      setUser(newUser);
    } catch (e) {
      throw e;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut();
      setUser(null);
    } catch (e) {
      throw e;
    }
  };

  const sendPasswordResetEmail = async (email) => {
    try {
      await firebaseSendPasswordResetEmail(email);
    } catch (e) {
      throw e;
    }
  };

  const confirmPasswordReset = async (code, password) => {
    try {
      await firebaseConfirmPasswordReset(code, password);
    } catch (e) {
      throw e;
    }
  };

  const emailPasswordReset = async (email) => {
    try {
      await firebaseRestorePassword(email);
      if (selectedEnglishLanguage)
        notification.info({
          style: { fontWeight: 500, fontSize: 17 },
          message: 'Message Sent!',
          description: `An Email has been sent to ${email} with the instructions to change your password.`,
          duration: 15,
        });
      else
        notification.info({
          style: { fontWeight: 500, fontSize: 17 },
          message: '¡Correo enviado!',
          description: `Se ha enviado un mensaje a ${email} para restablecer tu contraseña.`,
          duration: 15,
        });
    } catch (e) {
      // console.log('Error emailPasswordReset');
      throw e;
    }
  };

  const setTokenAndUser = async (authUser) => {
    if (authUser) {
      // console.log("1 onAuthStateChanged", authUser)
      const findUser = await getOrCreateUser(authUser);
      // console.log("2 onAuthStateChanged user", user)
      setUser(findUser);
    } else {
      setUser(null);
    }
  };

  useEffect(() => {
    const unsubscribe = app.auth().onAuthStateChanged(setTokenAndUser);
    return () => unsubscribe();
  }, []);

  // force refresh token every 25 minutes
  useEffect(() => {
    const forceRefreshToken = setInterval(async () => {
      const currentUser = await firebaseGetCurrentUser();
      if (currentUser) {
        await setRequestsToken(currentUser);
        console.log('Refreshing token');
      }
    }, 25 * 60 * 1000);
    // clean up setInterval
    return () => clearInterval(forceRefreshToken);
  }, []);

  return {
    isLoading,
    user,
    signIn,
    signUp,
    signOut,
    accessToken,
    signInProvider,
    sendPasswordResetEmail,
    confirmPasswordReset,
    emailPasswordReset,
  };
}

export const AuthProvider = ({ children }) => {
  const { isLoading, ...auth } = useProvideAuth();
  const [isMainLoading, setIsMainLoading] = useState(true);

  useEffect(() => {
    setTimeout(() => setIsMainLoading(false), 1000);
  }, []);

  if (isMainLoading || isLoading) return <BupLoading />;

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
